import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api, { setGlobalLogoutState, clearGlobalLogoutState } from '../utils/api';
import { API_ENDPOINTS } from '../utils/apiUrls';
import { isCustomerOnboardingRoute } from '../utils/customerRoutes';

/**
 * Hook to track user activity and auto-logout after specified inactive time
 * @param {number} timeoutMinutes - Minutes of inactivity before logout (default: 15)
 */
const useActivityTracker = (timeoutMinutes = 15) => {
    const navigate = useNavigate();
    const timeoutRef = useRef(null);
    const warningRef = useRef(null);
    const isAuthenticated = localStorage.getItem('token');

    // Convert minutes to milliseconds
    const TIMEOUT_MS = timeoutMinutes * 60 * 1000;
    const WARNING_MS = 60000; // Show warning 1 minute before logout

    const logout = useCallback(() => {
        // A customer filling an onboarding form is not a staff session, so a pause to
        // find a document must not send them to the staff login page.
        if (isCustomerOnboardingRoute()) return;
        const token = localStorage.getItem('token');
        if (token) {
            // End the session on the server too, so its token stops working at once (14 Oct 2026).
            api.post(API_ENDPOINTS.logoutUser, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
            // Set global flag BEFORE clearing storage to prevent race conditions
            // where pending API calls try to refresh token after logout
            setGlobalLogoutState(true);

            toast.info('You have been logged out due to inactivity.', {
                toastId: 'auto-logout',
                autoClose: 5000,
            });
            localStorage.clear();
            navigate('/login');
        }
    }, [navigate]);

    const showWarning = useCallback(() => {
        const token = localStorage.getItem('token');
        if (token) {
            toast.warn('You will be logged out in 1 minute due to inactivity. Please interact with the page to stay logged in.', {
                toastId: 'inactivity-warning',
                autoClose: 55000,
            });
        }
    }, []);

    const resetTimer = useCallback(() => {
        // Clear existing timers
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        if (warningRef.current) {
            clearTimeout(warningRef.current);
        }

        // Only set timers if user is authenticated - and never for a customer on an
        // onboarding link, who would otherwise see a staff inactivity warning.
        const token = localStorage.getItem('token');
        if (!token || isCustomerOnboardingRoute()) return;

        // Clear global logout state on activity (user is active again)
        clearGlobalLogoutState();

        // Set warning timer (1 minute before logout)
        warningRef.current = setTimeout(() => {
            showWarning();
        }, TIMEOUT_MS - WARNING_MS);

        // Set logout timer
        timeoutRef.current = setTimeout(() => {
            logout();
        }, TIMEOUT_MS);
    }, [logout, showWarning, TIMEOUT_MS, WARNING_MS]);

    useEffect(() => {
        // Only track activity if user is authenticated
        if (!isAuthenticated) return;

        // Events to track for user activity
        const events = [
            'mousedown',
            'mousemove',
            'keypress',
            'scroll',
            'touchstart',
            'click',
            'keydown',
            'wheel',
            'resize',
        ];

        // Add event listeners
        const handleActivity = () => {
            resetTimer();
        };

        events.forEach(event => {
            document.addEventListener(event, handleActivity, { passive: true });
        });

        // Initial timer setup
        resetTimer();

        // Handle visibility change (when user switches tabs)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                // Check if session expired while tab was inactive
                const token = localStorage.getItem('token');
                if (!token) {
                    navigate('/login');
                    return;
                }
                // Clear logout state when tab becomes visible (user is active)
                clearGlobalLogoutState();
                resetTimer();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Cleanup
        return () => {
            events.forEach(event => {
                document.removeEventListener(event, handleActivity);
            });
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            if (warningRef.current) {
                clearTimeout(warningRef.current);
            }
        };
    }, [isAuthenticated, navigate, resetTimer]);

    // Also reset timer when window is focused
    useEffect(() => {
        if (!isAuthenticated) return;

        const handleFocus = () => {
            // Check if token still exists
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }
            // Clear logout state on focus
            clearGlobalLogoutState();
            resetTimer();
        };

        window.addEventListener('focus', handleFocus);

        return () => {
            window.removeEventListener('focus', handleFocus);
        };
    }, [isAuthenticated, navigate, resetTimer]);

    return { resetTimer };
};

export default useActivityTracker;
